import { useState, type FormEvent } from 'react';
import { postSubscribe, StatusApiError } from '../api/statusApi';
import { IconBell, IconMail, IconRss } from './icons';
import './SubscribeForm.css';

type Tab = 'email' | 'rss';
type SubmitState = 'idle' | 'submitting' | 'success' | 'error';

export function SubscribeForm() {
  const [tab, setTab] = useState<Tab>('email');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<SubmitState>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!email.trim()) return;
    setState('submitting');
    setErrorMessage(null);
    try {
      await postSubscribe({ channel: 'email', target: email.trim() });
      setState('success');
      setEmail('');
    } catch (err) {
      setState('error');
      setErrorMessage(err instanceof StatusApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  return (
    <section className="subscribe-form card" aria-label="Subscribe to updates">
      <div className="subscribe-form__heading">
        <IconBell />
        <h2>Get notified</h2>
      </div>
      <div className="subscribe-form__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'email'}
          className={`subscribe-form__tab${tab === 'email' ? ' subscribe-form__tab--active' : ''}`}
          onClick={() => setTab('email')}
        >
          <IconMail width={16} height={16} /> Email
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'rss'}
          className={`subscribe-form__tab${tab === 'rss' ? ' subscribe-form__tab--active' : ''}`}
          onClick={() => setTab('rss')}
        >
          <IconRss width={16} height={16} /> RSS
        </button>
      </div>

      {tab === 'email' ? (
        <form className="subscribe-form__form" onSubmit={(e) => void handleSubmit(e)}>
          <label htmlFor="subscribe-email" className="visually-hidden">
            Email address
          </label>
          <input
            id="subscribe-email"
            type="email"
            required
            placeholder="you@district.edu"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={state === 'submitting'}
          />
          <button type="submit" disabled={state === 'submitting'}>
            {state === 'submitting' ? 'Subscribing…' : 'Subscribe'}
          </button>
          {state === 'success' && (
            <p className="subscribe-form__message subscribe-form__message--success" role="status">
              Check your inbox to confirm your subscription.
            </p>
          )}
          {state === 'error' && (
            <p className="subscribe-form__message subscribe-form__message--error" role="alert">
              {errorMessage}
            </p>
          )}
        </form>
      ) : (
        <div className="subscribe-form__rss">
          <p>Prefer a feed reader? Subscribe via RSS for every incident update.</p>
          <a className="subscribe-form__rss-link" href="/rss.xml">
            <IconRss width={16} height={16} /> Subscribe via RSS
          </a>
        </div>
      )}
    </section>
  );
}
